import { useMemo, useState } from "react";
import { DeliveryInfo } from "./delivery-info";
import { PurchaseSale } from "./purchase-sale";
import { RentalSale } from "./rental-sale";
import type { CustomRequired } from "~/@types/utility-types";
import { generateBackUrl } from "~/components/templates/common/purchase-modal/util/generate-back-url";
import { runningInBrowser } from "~/const/browser";
import { usePurchaseModalActions } from "~/hooks/use-purchase-modal-actions";
import type { G_FetchVideoContentQuery } from "~/lib/graphql";
import { usePostPurchaseVideoByCashierMutation, G_VideoPpvSaleType, G_VideoSeasonType } from "~/lib/graphql";
import { sendTrackingAddToCart } from "~/lib/tracking/events";
import { useAuth } from "~/provider/auth";
import { getDevice } from "~/util/get-device";

export type PostPurchaseVideoByCashierUrlArgs = {
  productId: string;
  productName: string;
  trackingSendPrice: number;
};

type PurchaseMainContentProps = {
  seasonType: G_VideoSeasonType;
  isWakuwari: boolean | undefined;
  ppvProducts?: CustomRequired<G_FetchVideoContentQuery["videoContent"]>["ppvProducts"];
};

/**
 * 購入モーダルの「販売パターンに紐づく情報」を表示するコンポーネント
 *
 * @機能要件
 * - 条件1：レンタル > 購入の順に上から並べること
 * - 条件2：単話 > パック > 全話の順に並べること（値段が高いのを下・値段が安いのを上）
 * - 条件3：画質の高い順に並べること（4K > HD > SD）
 * - 条件4：舞台の場合、必ず「購入のみ」を表示するようにすること
 */
export const PurchaseMainContent = (props: PurchaseMainContentProps) => {
  const { seasonType, isWakuwari, ppvProducts } = props;

  const { isLoggedIn, execAuth } = useAuth();
  const { openWakuwakuModalContent } = usePurchaseModalActions();

  // MEMO: network-only の場合、useMutation の loading が cache を見ないため
  const [isLoadingPurchaseVideoByCashier, setIsLoadingPurchaseVideoByCashier] = useState(false);

  // MEMO（kimura）: 前提として、レンタル期限、購入期限、視聴期限などの期限が存在する
  // MEMO（kimura）: キャッシュされてしまうと、最悪期限が切れてしまうため、fetchPolicyを`network-only`に変更
  // MEMO（kimura）: purchaseVideoByCashierのMutationはログイン必須なので、未ログイン状態で叩くと認証エラーになる
  const [purchaseVideoByCashierExec] = usePostPurchaseVideoByCashierMutation({
    fetchPolicy: "network-only",
  });

  const postPurchaseVideoByCashierUrl = async (args: PostPurchaseVideoByCashierUrlArgs) => {
    const { productId, productName } = args;

    if (isLoadingPurchaseVideoByCashier) return;
    setIsLoadingPurchaseVideoByCashier(true);

    if (!isLoggedIn) {
      execAuth();
      return;
    }

    if (isWakuwari) {
      openWakuwakuModalContent(productId);
      return;
    }

    if (!runningInBrowser) return;

    const seasonId = ppvProducts && ppvProducts.length !== 0 ? ppvProducts[0].seasonId : undefined;
    const contentId = ppvProducts && ppvProducts.length !== 0 ? ppvProducts[0].contentId : undefined;

    const currentUrl = new URL(location.href);
    const { search } = currentUrl;
    /** 末端上で購入モーダルが開かれている時trueを返すフラグ */
    const isOnDetail = search.includes("season");

    const currentParams = currentUrl.searchParams;
    currentParams.delete("purchaseContent");

    const backUrl = generateBackUrl({ seasonId, contentId, search, seasonType, isOnDetail, currentUrl });

    const { data } = await purchaseVideoByCashierExec({
      variables: {
        input: { device: getDevice(), productId, backUrl },
      },
    });

    setIsLoadingPurchaseVideoByCashier(false);

    if (!data) return;

    location.href = data.purchaseVideoByCashier.cashierUrl;

    // トラッキング（カート追加）
    sendTrackingAddToCart({
      seasonId: productId,
      item_name: productName,
    });
  };

  /**
   * ### saleType が TVOD（レンタル）作品の場合
   * - 単話 > パック > 全話 の順番で並べ替える
   * - 画質の高い順で並べ替える
   */
  const rentalData = useMemo(() => {
    return ppvProducts
      ?.filter(({ saleType }) => saleType === G_VideoPpvSaleType.Tvod)
      .sort((a, b) => a.contentPriority - b.contentPriority)
      .sort((a, b) => a.saleUnitPriority - b.saleUnitPriority);
  }, [ppvProducts]);

  /**
   * ### saleType が EST（購入）作品の場合
   * - 単話 > パック > 全話 の順番で並べ替える
   * - 画質の高い順で並べ替える
   */
  const purchaseData = useMemo(() => {
    return ppvProducts
      ?.filter(({ saleType }) => saleType === G_VideoPpvSaleType.Est)
      .sort((a, b) => a.contentPriority - b.contentPriority)
      .sort((a, b) => a.saleUnitPriority - b.saleUnitPriority);
  }, [ppvProducts]);

  const bundleData = useMemo(() => {
    if (ppvProducts === undefined || ppvProducts === null) return [];

    return ppvProducts.flatMap((item) => {
      if (!item.isBundleParent) return [];
      if (item.bundleProducts === null || item.bundleProducts === undefined) return [];
      return item.bundleProducts;
    });
  }, [ppvProducts]);

  return (
    <>
      {rentalData !== undefined && rentalData.length > 0 && (
        <RentalSale
          seasonType={seasonType}
          rentalData={rentalData}
          postPurchaseVideoByCashierUrl={postPurchaseVideoByCashierUrl}
        />
      )}

      {purchaseData !== undefined && purchaseData.length > 0 && (
        <PurchaseSale
          seasonType={seasonType}
          purchaseData={purchaseData}
          postPurchaseVideoByCashierUrl={postPurchaseVideoByCashierUrl}
        />
      )}

      {seasonType === G_VideoSeasonType.Stage && bundleData.length > 0 && <DeliveryInfo bundleData={bundleData} />}
    </>
  );
};
